import axios from 'axios';

const API_BASE = process.env.REACT_APP_QUOTE_API_URL || '';

/**
 * Fetch quote data by deal ID.
 * @param {string} dealId
 * @param {object} [opts]
 * @param {'quote'|'agreement'} [opts.context] passed through so the API
 *   can distinguish signing-page views from signed-agreement permalink
 *   views in its access log.
 * @returns {Promise<object>} Quote data
 */
export const fetchQuote = async (dealId, opts = {}) => {
  if (['commercial', 'commercial-agreement'].includes(opts.context)) {
    const response = await axios.get(`${API_BASE}/api/quote/commercial-read`, { params: { dealId } });
    return response.data;
  }
  const params = { ...(opts.context ? { context: opts.context } : {}),
    ...(opts.revisionCheck ? { revisionCheck: true } : {}) };
  const response = await axios.get(`${API_BASE}/api/quote/${dealId}`, { params });
  return response.data;
};

/**
 * Download the server-rendered proposal PDF for an open commercial quote.
 * @param {string} dealId - "<id>-<token>" from the proposal URL
 * @returns {Promise<Blob>}
 */
export const fetchCommercialPdf = async (dealId) => {
  const brand = new URLSearchParams(window.location.search).get('brand');
  const response = await axios.get(`${API_BASE}/api/quote/commercial-pdf`, {
    params: { dealId, ...(brand ? { brand } : {}) },
    responseType: 'blob',
    timeout: 60000,
  });
  return response.data;
};

/**
 * Poll the quote read endpoint until it reports the terminal `signed`
 * state, or the attempt budget runs out. Used only to recover the
 * customer-facing success screen after a submit timeout (see submitQuote):
 * the submit handler writes status='signed' before it responds, so once
 * the server-side critical path finishes, this read sees `signed` even
 * though the client already aborted the POST.
 *
 * Each read is tagged context=submit-poll so it is distinguishable in the
 * access log; the read endpoint already skips the Sell "quote viewed"
 * note for signed/signing quotes, so this does not spam ops. Best-effort:
 * any read error just counts as "not signed yet" and we try again.
 */
const pollForSigned = async (dealId, { attempts = 8, intervalMs = 2000 } = {}) => {
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    try {
      const { data } = await axios.get(`${API_BASE}/api/quote/${dealId}`, {
        params: { context: 'submit-poll' },
        timeout: 8000,
      });
      if (data && data.status === 'signed') return true;
    } catch {
      /* transient read failure: treat as not-yet-signed and retry */
    }
  }
  return false;
};

/**
 * Submit signed quote with payment info, signature, and access details
 * @param {string} dealId
 * @param {object} payload - { signature, paymentMethod, paymentInfo, billingAddress, accessInfo }
 * @param {object} [opts]
 * @param {boolean} [opts.pollForSignedOnTimeout] when true, a client-side
 *   timeout does not throw immediately; instead we poll the read endpoint
 *   for the signed state and resolve success if the submit actually landed
 *   server-side. Gated by the caller (Statsig submit_poll_on_timeout).
 * @returns {Promise<object>} Submission result
 */
export const submitQuote = async (dealId, payload, opts = {}) => {
  const { pollForSignedOnTimeout = false } = opts;
  // 20s ceiling on the submit POST. Normal end-to-end submit completes well
  // under 10s. The customer-facing critical path is now just QBO card store
  // plus the $0 auth (customer create is ~0.65s; ticket, recurring template,
  // Sell stage, and confirmation email are all deferred server-side to
  // runPostsignAsync), but on a cold start or a slow-day QBO that path can
  // still cross the old 15s ceiling. 20s buys headroom, and
  // pollForSignedOnTimeout below covers the tail past 20s so a slow-but-
  // successful signing never dumps the customer on an error screen.
  const post = () => axios.post(`${API_BASE}/api/quote/submit`, {
    dealId,
    ...payload,
  }, {
    timeout: 20000,
  });

  try {
    const response = await post();
    return response.data;
  } catch (err) {
    // Client-side timeout recovery. A timeout almost always means the
    // server received the request and is still finishing a submit that
    // will succeed (submit.js writes status='signed' before responding).
    // Rather than showing the customer an error while their signup is in
    // fact completing (the "timeout of 15000ms exceeded" false errors ops
    // kept getting paged about), poll the read endpoint for the signed
    // state and resolve success if it lands. If it never reaches signed
    // within the poll window, fall through to the original timeout error
    // so a genuinely failed submit still surfaces.
    if (err.code === 'ECONNABORTED' && pollForSignedOnTimeout) {
      const signed = await pollForSigned(dealId);
      if (signed) {
        return { success: true, recoveredViaPoll: true };
      }
      throw err;
    }
    // Auto-retry once after a 2s backoff on a transient failure:
    //   - a bare network error (TCP/TLS failure, no response) -- a mobile
    //     connection blip mid-upload; or
    //   - a 5xx response -- a transient server/Vercel/QBO blip. Seth
    //     Struiksma (deal 199697952, 2026-06-10) got a bare 500 that
    //     succeeded on his manual reload; this makes that recovery
    //     automatic instead of dumping him on an "Internal Server Error".
    // The submit handler is idempotent: status='signed' replays the stored
    // result, and a durable KV SET-NX lock guards in-flight submissions,
    // so a retry can't double-fire QBO/Sell/Zendesk even if the first
    // request actually reached the server.
    //
    // Timeouts (ECONNABORTED) are not retried here: the customer already
    // waited the full 20s (and, when the gate is on, was handled by the
    // poll-for-signed branch above), and a timeout almost always means the server
    // received the request and is still processing it. A retry would
    // race the in-flight KV lock and surface 409 to the customer. 4xx
    // (validation, 402 decline, 404, 409) is also not retried -- a retry
    // can't fix it.
    const isBareNetworkError = !err.response && err.code !== 'ECONNABORTED';
    const isServerError = !!err.response && err.response.status >= 500;
    if (!isBareNetworkError && !isServerError) throw err;

    await new Promise((resolve) => setTimeout(resolve, 2000));
    const response = await post();
    return response.data;
  }
};

/**
 * Submit a signed COMMERCIAL service agreement. Distinct from
 * submitQuote: no payment info (commercial is invoiced), and it carries
 * the authorized-signer name + title. The commercial submit handler is
 * idempotent the same way (status='signed' replays; durable KV lock).
 * @param {string} dealId
 * @param {object} payload - { signerName, signerTitle, contactEmail, howHeard }
 */
export const submitCommercial = async (dealId, payload) => {
  const post = () => axios.post(`${API_BASE}/api/quote/commercial-submit`, {
    dealId,
    ...payload,
  }, { timeout: 15000 });
  try {
    const response = await post();
    return response.data;
  } catch (err) {
    const isBareNetworkError = !err.response && err.code !== 'ECONNABORTED';
    const isServerError = !!err.response && err.response.status >= 500;
    if (!isBareNetworkError && !isServerError) throw err;
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const response = await post();
    return response.data;
  }
};

/**
 * Submit "How did you hear?" attribution after the customer signs.
 * Adds a note to the deal in Zendesk Sell.
 */
export const submitHowHeard = async (dealId, howHeard) => {
  const response = await axios.post(`${API_BASE}/api/quote/howheard`, {
    dealId,
    howHeard,
  });
  return response.data;
};

/**
 * Tell the backend a customer hit a non-trivial error so ops gets an
 * alert ticket. Fire-and-forget: we never want this to break the
 * customer's flow, so the call is best-effort and any error here is
 * swallowed.
 */
export const reportError = async (payload) => {
  try {
    await axios.post(`${API_BASE}/api/quote/report-error`, payload, {
      timeout: 5000,
    });
  } catch {
    /* swallow -- never block the customer on alert delivery */
  }
};

/**
 * Fetch the regional list of specialty treatments billed separately.
 * Called from QuotePage so the agreement card stays current with the
 * pricing engine config without a redeploy when prices change.
 */
export const fetchSpecialtyExtras = async ({ brand = 'Cabana', zipCode, location, spaConstruction }) => {
  const body = location ? { brand, location } : { brand, zipCode };
  // spaConstruction ('above_ground' | 'in_ground') lets the pricing engine
  // drop filter cleaning for above-ground spas and PoolRX for all spa-only
  // deals. Omitted for pool deals so the engine returns the full list.
  if (spaConstruction) body.spaConstruction = spaConstruction;
  const response = await axios.post(`${API_BASE}/api/specialty-extras`, body);
  return response.data;
};
